/**
 * Pending Re-weigh Queue — bales scanned at blowroom but missing
 * actual weight + moisture. Operators do batch capture at end of shift.
 *
 * Each row is one execution line. Operator types in actual_weight_kg
 * (read from weighbridge) and optionally actual_moisture_pct (from
 * the moisture meter), then clicks Save row OR Save All.
 *
 * Source: GET /bale-mgmt/reweigh/pending
 *         PATCH /bale-mgmt/reweigh/{line_id}
 *         POST /bale-mgmt/reweigh/batch
 */
import { useEffect, useMemo, useState } from 'react'
import {
  App, Button, Card, DatePicker, Empty, InputNumber, Space, Table, Tag, Typography,
} from 'antd'
import { CheckOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography
const { RangePicker } = DatePicker

interface PendingRow {
  line_id: string
  laydown_no: string
  actual_bale_no: string | null
  planned_weight_kg: number | null
  planned_moisture_pct: number | null
  scan_timestamp: string | null
}

export default function PendingReweighPage() {
  const { plantId } = usePlantId()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [
    dayjs().subtract(7, 'day'),
    dayjs(),
  ])
  // edits keyed by line_id; stays in memory until user hits Save
  const [edits, setEdits] = useState<Record<string, { weight?: number; moisture?: number }>>({})

  const { data: rows = [], isLoading, refetch } = useQuery<PendingRow[]>({
    queryKey: [
      'pending-reweigh', plantId,
      range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'),
    ],
    queryFn: () =>
      apiClient
        .get('/bale-mgmt/reweigh/pending', {
          params: {
            plant_id: plantId,
            from_date: range[0].format('YYYY-MM-DD'),
            to_date: range[1].format('YYYY-MM-DD'),
          },
        })
        .then(r => r.data),
    enabled: !!plantId,
  })

  // Reset edits whenever the underlying list refreshes — saved rows
  // disappear from the queue and stale edits would point to nothing.
  useEffect(() => {
    setEdits({})
  }, [rows.length])

  const saveOne = useMutation({
    mutationFn: ({ line_id, weight, moisture }: { line_id: string; weight: number; moisture?: number }) =>
      apiClient
        .patch(`/bale-mgmt/reweigh/${line_id}`, {
          actual_weight_kg: weight,
          actual_moisture_pct: moisture ?? null,
        })
        .then(r => r.data),
    onSuccess: () => {
      message.success('Saved')
      qc.invalidateQueries({ queryKey: ['pending-reweigh'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Save failed'),
  })

  const saveAll = useMutation({
    mutationFn: () => {
      const updates = Object.entries(edits)
        .filter(([_, v]) => v?.weight != null)
        .map(([line_id, v]) => ({
          line_id,
          actual_weight_kg: v.weight!,
          actual_moisture_pct: v.moisture ?? null,
        }))
      return apiClient.post('/bale-mgmt/reweigh/batch', { updates }).then(r => r.data)
    },
    onSuccess: (res: any) => {
      message.success(`Saved ${res.ok} of ${res.total}`)
      if (res.failed?.length) {
        message.warning(`${res.failed.length} failed — see console`)
        console.warn('Reweigh failures:', res.failed)
      }
      qc.invalidateQueries({ queryKey: ['pending-reweigh'] })
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Batch save failed'),
  })

  // count rows that have an edit ready to save
  const readyCount = useMemo(
    () => Object.values(edits).filter(v => v?.weight != null).length,
    [edits],
  )

  return (
    <div>
      <PageHeader
        title="Pending Re-weigh Queue"
        subtitle="Bales scanned at the blowroom but missing actual weight / moisture. Capture at end of shift."
        extra={
          <Space>
            <RangePicker
              value={range}
              onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])}
              format="DD-MMM-YYYY"
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              disabled={readyCount === 0}
              loading={saveAll.isPending}
              onClick={() => saveAll.mutate()}
            >
              Save All ({readyCount})
            </Button>
          </Space>
        }
      />

      <Card size="small">
        {!plantId ? <Empty description="Select a plant" /> :
          rows.length === 0 && !isLoading ? (
            <Empty description="No bales pending re-weigh in this window — every scan has actual weight captured." />
          ) : (
            <Table
              size="small"
              rowKey="line_id"
              loading={isLoading}
              dataSource={rows}
              pagination={{ pageSize: 50, showTotal: t => `${t} pending` }}
              columns={[
                { title: 'Laydown', dataIndex: 'laydown_no', width: 120,
                  render: (v: string) => <Text code>{v}</Text> },
                { title: 'Bale No', dataIndex: 'actual_bale_no', width: 130,
                  render: (v?: string) => v || '—' },
                { title: 'Scanned', dataIndex: 'scan_timestamp', width: 140,
                  render: (v?: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—' },
                { title: 'Godown kg', dataIndex: 'planned_weight_kg', width: 100, align: 'right' as const,
                  render: (v?: number) => v ? v.toFixed(1) : '—' },
                { title: 'Godown moist %', dataIndex: 'planned_moisture_pct', width: 110, align: 'right' as const,
                  render: (v?: number) => v ? v.toFixed(2) : '—' },
                { title: 'Actual kg', key: 'actual_weight', width: 130,
                  render: (_: unknown, r: PendingRow) => (
                    <InputNumber
                      size="small"
                      placeholder="From scale"
                      step={0.1}
                      min={0}
                      style={{ width: '100%' }}
                      value={edits[r.line_id]?.weight}
                      onChange={(value) => setEdits(prev => ({
                        ...prev,
                        [r.line_id]: { ...prev[r.line_id], weight: value as number | undefined },
                      }))}
                    />
                  ) },
                { title: 'Actual moist %', key: 'actual_moisture', width: 130,
                  render: (_: unknown, r: PendingRow) => (
                    <InputNumber
                      size="small"
                      placeholder="Optional"
                      step={0.1}
                      min={0}
                      max={20}
                      style={{ width: '100%' }}
                      value={edits[r.line_id]?.moisture}
                      onChange={(value) => setEdits(prev => ({
                        ...prev,
                        [r.line_id]: { ...prev[r.line_id], moisture: value as number | undefined },
                      }))}
                    />
                  ) },
                { title: 'Action', key: 'act', width: 90, fixed: 'right' as const,
                  render: (_: unknown, r: PendingRow) => {
                    const e = edits[r.line_id]
                    return (
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        disabled={e?.weight == null}
                        loading={saveOne.isPending}
                        onClick={() =>
                          saveOne.mutate({ line_id: r.line_id, weight: e!.weight!, moisture: e?.moisture })
                        }
                      >
                        Save
                      </Button>
                    )
                  } },
              ]}
            />
          )}
      </Card>

      <Card size="small" style={{ marginTop: 12 }}>
        <Space direction="vertical" size={4}>
          <Tag color="blue">Hint</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Read the actual kg from the weighbridge as each bale is fed. Moisture % is optional —
            enter only if you have a moisture meter at the feed point. The system automatically
            computes dry-weight loss vs godown so you can spot bales that lost more weight in storage
            than the lot average.
          </Text>
        </Space>
      </Card>
    </div>
  )
}
